import { FC, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  parsePathForEntityUUID,
  pbUUIDToUuid,
  uuidToPbUUID,
  CENTRAL_CONTAINER_GAP,
} from "../utils/utils"
import { Link, useHistory, useLocation } from "react-router-dom"
import {
  AutoComplete,
  Button,
  Col,
  DatePicker,
  Divider,
  Input,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Typography,
  message,
} from "antd"
import dayjs from "dayjs"
import { FlexCol, FlexRow, UnderlinedSectionTitle } from "../components/commons-ts/common"
import { ErrorAlert } from "../components/commons-ts/errorAlert"
import { SimplifiedBreadcrumb } from "../components/commons-ts/simplifiedBreadcrumb"
import { Label, LabeledInput } from "../components/commons-ts/input"
import { Translated } from "../utils/translated"
import {
  normTypeElement,
  methodElement,
  isLabTestElement,
  normTypeDescriptionStr,
  methodDescriptionStr,
} from "../components/commons-ts/tags"
import {
  ConsoleSqlOutlined,
  DeleteOutlined,
  FileAddOutlined,
  FilePdfOutlined,
  SaveOutlined,
} from "@ant-design/icons"
import {
  Annex,
  Equipment,
  MethodTypeEN1177,
  NormType,
  Site,
  UUID,
} from "../generated/proto-ts/main"

import {
  useUsercommUpdateAnnexBimodal,
  useUsercommDeleteAnnexBimodal,
  useUsercommCreateEquipmentBimodal,
  useUsercommSiteBimodal,
  useUsercommSiteAnnexesBimodal,
  useUsercommCreateAnnexBimodal,
  useUsercommSiteEquipmentsBimodal,
  useUsercommDeleteSiteBimodal,
  useUsercommUpdateSiteBimodal,
  useUsercommSiteChildrenRecursiveBimodal,
} from "../usercomm/common/usercommAsyncRequestBimodal"

import { UploadImageListV2 } from "../components/commons-ts/uploadImageV2"
import { DataTreeDrawer } from "../components/commons-ts/dataTreeDrawer"
import { SiteEquipmentsTableSM } from "./Tables/01_SiteEquipments/SiteEquipmentsTable_SM"
import {
  SiteEquipmentsTablePF_ADQ,
  SiteEquipmentsTablePF_CFH,
} from "./Tables/01_SiteEquipments/SiteEquipmentsTable_PF"
import { SiteEquipmentsTable_AB } from "./Tables/01_SiteEquipments/SiteEquipmentsTable_AB"
import { SiteEquipmentsTable_TL } from "./Tables/01_SiteEquipments/SiteEquipmentsTable_TL"
import { useTranslationContext } from "../providers/translationProvider"
import { v4 as uuidv4 } from "uuid"
import { useCloudUsercommSiteChildrenRecursiveWS } from "../usercomm/cloud/cloudUsercommAsyncRequestWS"
import { useDebounce } from "../utils/debounce"
import { EntityTimestamps } from "../components/commons-ts/entityTimestamps"
import { ICloudCustomer, ICloudUser } from "../types"
import { callCloudApiV2 } from "../utils/cloudApiV2"
import { useSyncronizationContext } from "../providers/syncronizationProvider"
import { GoogleAddressWidget, LabeledTsCoordinate } from "../components/commons-ts/googleMaps"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faExternalLink } from "@fortawesome/free-solid-svg-icons"
import Compact from "antd/es/space/Compact"

const AnnexWidget: FC<{
  annex: Annex | null
  idx: number
  getAnnexes: () => void
}> = ({ annex, idx, getAnnexes }) => {
  const [antdMessageCtx, contextHolder] = message.useMessage()
  const [annexContent, setAnnexContent] = useState<string | null>(null)
  const [_annexContent, _setAnnexContent] = useState<string | null>(null) // internal state
  const [pictures, setPictures] = useState<UUID[] | null>(null)

  const [updateAnnexResponse, updateAnnexRequest] = useUsercommUpdateAnnexBimodal()
  const [deleteAnnexResponse, deleteAnnexRequest] = useUsercommDeleteAnnexBimodal()

  const { translated } = useTranslationContext()

  useEffect(() => {
    if (annex === null) {
      return
    }
    setAnnexContent(annex.annex_content)
    setPictures(annex.pictures)
  }, [annex])

  const memoAnnexIsMutated = useMemo(() => {
    if (annex === null || annexContent === null) {
      return false
    }
    let prevAnnexContent = annex.annex_content

    let isMutated = (annexContent ?? "") !== prevAnnexContent
    if (pictures !== null) {
      let prevPicturesStr = annex.pictures.map((p) => pbUUIDToUuid(p)).join(",")
      let picturesStr = pictures.map((p) => pbUUIDToUuid(p)).join(",")
      if (prevPicturesStr !== picturesStr) {
        isMutated = true
        console.debug(`AnnexWidget: pictures changed: "${prevPicturesStr}" vs "${picturesStr}"`)
      }
    }
    console.debug(`AnnexWidget: isMutated: ${isMutated}`)
    return isMutated
  }, [annex, annexContent, pictures])

  // UPDATE ANNEX REQUEST
  const onUpdate = async () => {
    if (annex === null) {
      return
    }
    let updatedAnnex = annex.clone()
    updatedAnnex.annex_idx = idx
    updatedAnnex.annex_content = annexContent ?? ""
    if (pictures !== null) {
      updatedAnnex.pictures = pictures
    }
    updatedAnnex.updated_at = Date.now()
    updateAnnexRequest(updatedAnnex)
  }
  const debouncedOnUpdate = useDebounce(onUpdate)
  // UPDATE ANNEX RESPONSE
  useEffect(() => {
    if (updateAnnexResponse === null) {
      return
    }
    console.log(`AnnexWidget: updateAnnexResponse`, updateAnnexResponse.toObject())
    // UPD 2024-09-06: do not re-fetch annexes to save bandwidth
    getAnnexes()
    antdMessageCtx.success(translated("Annex updated!"))
  }, [updateAnnexResponse])

  useEffect(() => {
    if (!memoAnnexIsMutated) {
      return
    }
    debouncedOnUpdate()
  }, [memoAnnexIsMutated, debouncedOnUpdate])

  // DELETE ANNEX REQUEST
  const onDelete = useCallback(async () => {
    if (annex === null) {
      return
    }
    deleteAnnexRequest(annex.uuid)
  }, [annex, idx])
  // DELETE ANNEX RESPONSE
  useEffect(() => {
    if (deleteAnnexResponse === null) {
      return
    }
    console.log(`AnnexWidget: deleteAnnexResponse`, deleteAnnexResponse.toObject())
    getAnnexes()
  }, [deleteAnnexResponse, idx])

  if (annex === null) {
    return <Spin size="large" />
  }

  return (
    <div>
      {contextHolder}
      <Label>
        <Translated keyEn="Annex" /> #{idx}
      </Label>
      <FlexCol style={{ gap: 5 }}>
        <Space.Compact>
          <Input
            size="large"
            value={_annexContent ?? ""}
            onChange={(e) => {
              _setAnnexContent(e.target.value)
            }}
            onBlur={() => {
              setAnnexContent(_annexContent)
            }}
            placeholder={translated("Annex content") ?? ""}
            variant="filled"
          />
          <Popconfirm
            title={
              <>
                <Translated keyEn="Are you sure you want to delete this annex" />?
              </>
            }
            onConfirm={onDelete}
          >
            <Button icon={<DeleteOutlined />} size="large" danger />
          </Popconfirm>
          <Button
            icon={<SaveOutlined />}
            onClick={onUpdate}
            size="large"
            type="primary"
            disabled={!memoAnnexIsMutated}
          />
        </Space.Compact>
        {pictures !== null && <UploadImageListV2 pictures={pictures} setPictures={setPictures} />}
      </FlexCol>
    </div>
  )
}

const NewEquipmentButton: FC<{
  siteUUID: UUID | null
  equipmentName: string | null
}> = ({ siteUUID: siteUUID, equipmentName }) => {
  const [antdMessageCtx, contextHolder] = message.useMessage()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const history = useHistory()

  const [createEquipmentNewUUID, createEquipmentRequest] = useUsercommCreateEquipmentBimodal()

  // CREATE EQUIPMENT REQUEST
  const onCreate = useCallback(async () => {
    setIsLoading(true)
    if (siteUUID === null) {
      throw new Error("siteUUID is null")
    }
    if (equipmentName === null) {
      throw new Error("equipmentName is null")
    }
    let ts = Date.now()
    createEquipmentRequest(
      siteUUID,
      new Equipment({
        uuid: uuidToPbUUID(uuidv4()), // IT IS CREATOR'S RESPONSIBILITY TO GENERATE A NEW UUID
        site_uuid: siteUUID,
        equipment_name: equipmentName,
        created_at: ts,
        updated_at: ts,
      }),
    )
  }, [siteUUID, equipmentName])
  // CREATE EQUIPMENT RESPONSE
  useEffect(() => {
    if (createEquipmentNewUUID === null) {
      return
    }
    console.log(`NewEquipmentButton: createEquipmentNewUUID`, createEquipmentNewUUID.toObject())
    setIsLoading(false)
    if (createEquipmentNewUUID.value) {
      antdMessageCtx.success(
        <>
          Equipment <b>{equipmentName}</b> was successfully created!
        </>,
      )
      history.push(`/equipments/${pbUUIDToUuid(createEquipmentNewUUID)}`)
    }
  }, [createEquipmentNewUUID])

  return (
    <>
      {/* antd message context holder */}
      {contextHolder}
      <Button
        disabled={siteUUID === null || equipmentName === null}
        onClick={onCreate}
        loading={isLoading}
        size="large"
        type="primary"
      >
        <span
          style={{
            textTransform: "uppercase",
          }}
        >
          <Translated keyEn="Start" />
        </span>
      </Button>
    </>
  )
}

export const SitePage: FC = () => {
  const [antdMessageCtx, contextHolder] = message.useMessage()
  const location = useLocation()
  const history = useHistory()

  const memoSiteUUID = useMemo((): UUID | null => {
    let siteUUidStr = parsePathForEntityUUID(location.pathname)
    if (siteUUidStr === null) {
      return null
    }
    return uuidToPbUUID(siteUUidStr)
  }, [location.pathname])

  const [site, getSite, externallySetSite] = useUsercommSiteBimodal()
  const [updateSiteAck, updateSite] = useUsercommUpdateSiteBimodal()
  const [deleteSiteAck, deleteSite] = useUsercommDeleteSiteBimodal()

  const [annexes, getAnnexes] = useUsercommSiteAnnexesBimodal()
  const [createAnnexNewUUID, createAnnexRequest] = useUsercommCreateAnnexBimodal()

  const [equipments, getEquipments] = useUsercommSiteEquipmentsBimodal()

  const [_, __, siteZoneImpactsMap, getSiteChildrenRecursive] =
    useCloudUsercommSiteChildrenRecursiveWS()

  const [enterpriseCustomers, setEnterpriseCustomers] = useState<ICloudCustomer[]>([])
  const [enterpriseUsers, setEnterpriseUsers] = useState<ICloudUser[]>([])
  const [siteUser, setSiteUser] = useState<ICloudUser | null>(null)

  const [siteName, setSiteName] = useState<string | null>(null)
  const [clientName, setClient] = useState<string | null>(null)
  const [clientUUID, setClientUUID] = useState<UUID | null>(null)
  const [missionName, setMission] = useState<string | null>(null)
  const [normType, setNormType] = useState<NormType | null>(null)
  const [methodType, setMethodType] = useState<MethodTypeEN1177 | null>(null)
  const [isLabTest, setIsLabTest] = useState<boolean | null>(null)
  const [address, setAddress] = useState<string | null>(null)
  const [executionDate, setExecutionDate] = useState<dayjs.Dayjs | null>(null)
  const [pictures, setPictures] = useState<UUID[] | null>(null)
  const [createdAt, setCreatedAt] = useState<number | null>(null)
  const [userUUID, setUserUUID] = useState<UUID | null>(null)

  // NormType / Method selectors state
  const [selectedNormTypeToChange, setSelectedNormTypeToChange] = useState<NormType | null>(null)
  const [selectedMethodTypeToChange, setSelectedMethodTypeToChange] =
    useState<MethodTypeEN1177 | null>(null)
  const [selectedUserUUID, setSelectedUserUUID] = useState<string | null>(null)

  const [newEquipmentName, setNewEquipmentName] = useState<string | null>(null)

  const { translated } = useTranslationContext()
  const { currentEnterprise } = useSyncronizationContext()

  // SITE REQUEST
  useEffect(() => {
    if (memoSiteUUID === null) {
      return
    }
    getSite(memoSiteUUID)
  }, [memoSiteUUID])
  // SITE RESPONSE
  useEffect(() => {
    if (site == null) {
      return
    }
    console.log(`SitePage: site`, site.toObject())
    setSiteName(site.site_name)
    setClient(site.client_name)
    setMission(site.mission_name)
    setNormType(site.norm_type)
    setSelectedNormTypeToChange(site.norm_type)
    setMethodType(site.method_type_en_1177)
    setSelectedMethodTypeToChange(site.method_type_en_1177)
    setIsLabTest(site.is_lab_test)
    setAddress(site.address)
    if (site.execution_date === 0) {
      setExecutionDate(null)
    } else {
      setExecutionDate(dayjs(site.execution_date))
    }
    setPictures(site.pictures)
    setCreatedAt(site.created_at)
    setUserUUID(site.user_uuid)
    setSelectedUserUUID(pbUUIDToUuid(site.user_uuid))
  }, [site])

  // ANNEXES REQUEST
  useEffect(() => {
    if (memoSiteUUID === null) {
      return
    }
    getAnnexes(memoSiteUUID)
  }, [memoSiteUUID])
  // ANNEXES RESPONSE
  useEffect(() => {
    if (annexes !== null) {
      console.log(
        `SitePage: anexes`,
        annexes.map((a) => a.toObject()),
      )
    }
  }, [annexes])

  // EQUIPMENTS REQUEST
  useEffect(() => {
    if (memoSiteUUID === null) {
      return
    }
    getEquipments(memoSiteUUID)
  }, [memoSiteUUID])
  // EQUIPMENTS RESPONSE
  useEffect(() => {
    if (equipments === null) {
      return
    }
    console.log(
      `SitePage: equipments`,
      equipments.map((e) => e.toObject()),
    )
  }, [equipments])

  // SITE CHILDREN (IMPACTS) REQUEST
  useEffect(() => {
    if (memoSiteUUID === null) {
      return
    }
    getSiteChildrenRecursive(memoSiteUUID)
  }, [memoSiteUUID])

  // CREATE ANNEX REQUEST
  const onAddAnnex = useCallback(async () => {
    if (memoSiteUUID === null) {
      return
    }
    let ts = Date.now()
    let annex = new Annex({
      uuid: uuidToPbUUID(uuidv4()), // IT IS CREATOR'S RESPONSIBILITY TO GENERATE A NEW UUID
      site_uuid: memoSiteUUID,
      created_at: ts,
      updated_at: ts,
    })
    createAnnexRequest(memoSiteUUID, annex)
  }, [memoSiteUUID])
  // NEW ANNEX RESPONSE
  useEffect(() => {
    if (createAnnexNewUUID === null) {
      return
    }
    if (createAnnexNewUUID.value) {
      antdMessageCtx.success(translated("New annex created!"))
    } else {
      antdMessageCtx.error(translated("Could not create annex!"))
    }
    getAnnexes(memoSiteUUID)
  }, [createAnnexNewUUID])

  // DELETE SITE REQUEST
  const onDelete = useCallback(async () => {
    if (memoSiteUUID === null) {
      return
    }
    deleteSite(memoSiteUUID)
  }, [memoSiteUUID])
  // DELETE SITE RESPONSE
  useEffect(() => {
    if (deleteSiteAck === null) {
      return
    }
    antdMessageCtx.info(translated("Site deleted!"))
    setTimeout(() => {
      history.push("/")
    }, 1000)
  }, [deleteSiteAck])

  useEffect(() => {
    if (normType !== NormType.EN_1177) {
      setMethodType(MethodTypeEN1177.UNKNOWN_METHOD_TYPE_EN_1177)
      setSelectedMethodTypeToChange(MethodTypeEN1177.UNKNOWN_METHOD_TYPE_EN_1177)
      return
    }
    if (
      normType === NormType.EN_1177 &&
      (methodType === null || methodType === MethodTypeEN1177.UNKNOWN_METHOD_TYPE_EN_1177)
    ) {
      setMethodType(MethodTypeEN1177.CRITICAL_FALL_HEIGHT_DETERMINATION)
      setSelectedMethodTypeToChange(MethodTypeEN1177.CRITICAL_FALL_HEIGHT_DETERMINATION)
      return
    }
  }, [normType, methodType, isLabTest])

  const memoNormTypeElement = useMemo(() => {
    return normTypeElement(normType)
  }, [normType])

  const memoMethodElement = useMemo(() => {
    if (normType === null || normType === NormType.EN_12503) {
      return null
    }
    return methodElement(methodType)
  }, [normType, methodType])

  const memoIsLabTestElement = useMemo(() => {
    return isLabTestElement(isLabTest)
  }, [isLabTest])

  const memoEquipmentsTable = useMemo(() => {
    if (normType === null) {
      return null
    }
    if (normType === NormType.EN_12503) {
      return <SiteEquipmentsTableSM siteUUID={memoSiteUUID} equipments={equipments} />
    } else if (normType === NormType.EN_14960) {
      return <SiteEquipmentsTable_AB equipments={equipments} siteUUID={memoSiteUUID} />
    } else if (normType === NormType.EN_ISO_23659) {
      return <SiteEquipmentsTable_TL equipments={equipments} siteUUID={memoSiteUUID} />
    } else if (normType === NormType.EN_1177) {
      if (methodType === MethodTypeEN1177.CRITICAL_FALL_HEIGHT_DETERMINATION) {
        return <SiteEquipmentsTablePF_CFH siteUUID={memoSiteUUID} equipments={equipments} />
      } else if (methodType === MethodTypeEN1177.IMPACT_ATTENUATION_COMPLIANCE) {
        return <SiteEquipmentsTablePF_ADQ siteUUID={memoSiteUUID} equipments={equipments} />
      }
    }
  }, [memoSiteUUID, equipments, normType, methodType])

  const getAnnexesCallback = useCallback(() => {
    getAnnexes(memoSiteUUID)
  }, [memoSiteUUID])

  const memoAnnexesElements = useMemo(() => {
    if (annexes === null) {
      return null
    }
    let children: ReactElement[] = []

    annexes.sort((a, b) => a.created_at - b.created_at)

    for (let i = 0; i < annexes.length; i++) {
      let annex = annexes[i]
      if (annex === undefined || annex.deleted_at > 0) {
        continue
      }
      children.push(
        <AnnexWidget
          annex={annex}
          idx={i + 1}
          getAnnexes={getAnnexesCallback}
          key={pbUUIDToUuid(annex.uuid)}
        />,
      )
      if (i !== annexes.length - 1) {
        children.push(<Divider key={`div-${i}`} />)
      }
    }
    return <div style={{ width: "100%" }}>{children}</div>
  }, [memoSiteUUID, annexes])

  const memoSiteIsMutated = useMemo(() => {
    // entity and created_at are only null when the entity is not loaded yet
    if (site === null || createdAt === null) {
      return false
    }
    let prevUserUUIDStr = pbUUIDToUuid(site.user_uuid)
    let currUserUUIDStr = userUUID !== null ? pbUUIDToUuid(userUUID) : null
    let userChanged = currUserUUIDStr !== null && prevUserUUIDStr !== currUserUUIDStr

    let prevExecutionDateInt: number = site.execution_date
    let currExecutionDateInt: number = 0
    if (executionDate !== null) {
      currExecutionDateInt = executionDate.toDate().getTime()
    }
    let executionDateChanged = prevExecutionDateInt !== currExecutionDateInt

    let isMutated =
      site.site_name !== (siteName || "") ||
      site.client_name !== (clientName || "") ||
      site.mission_name !== (missionName || "") ||
      site.norm_type !== normType ||
      site.method_type_en_1177 !== methodType ||
      site.is_lab_test !== isLabTest ||
      site.address !== (address || "") ||
      executionDateChanged ||
      userChanged

    if (pictures !== null) {
      let prevPicturesStr = site.pictures.map((u) => pbUUIDToUuid(u)).join(",")
      let currPicturesStr = pictures.map((u) => pbUUIDToUuid(u)).join(",")
      if (currPicturesStr !== prevPicturesStr) {
        isMutated = true
        console.debug(`Site: pictures mutated: "${currPicturesStr}" vs "${prevPicturesStr}"`)
      }
    }
    if (isMutated) {
      console.debug(
        `SitePage: isMutated:
            site: "${siteName}" vs "${site.site_name}"
            client: "${clientName}" vs "${site.client_name}"
            mission: "${missionName}" vs "${site.mission_name}"
            norm_type: "${normType}" vs "${site.norm_type}"
            method: "${methodType}" vs "${site.method_type_en_1177}"
            is_lab_test: "${isLabTest}" vs "${site.is_lab_test}"
            address: "${address}" vs "${site.address}"
            execution_date: "${currExecutionDateInt}" vs "${prevExecutionDateInt}"
            user_uuid: "${currUserUUIDStr}" vs "${prevUserUUIDStr}"
            `,
      )
    }
    return isMutated
  }, [
    site,
    siteName,
    clientName,
    clientUUID,
    missionName,
    normType,
    methodType,
    isLabTest,
    address,
    executionDate,
    pictures,
    userUUID,
  ])

  // UPDATE SITE REQUEST
  const onUpdate = async () => {
    if (site === null) {
      return
    }
    let updatedSite = site.clone()
    updatedSite.site_name = siteName ?? ""
    updatedSite.client_name = clientName ?? ""
    updatedSite.mission_name = missionName ?? ""
    updatedSite.norm_type = normType ?? NormType.EN_1177
    updatedSite.method_type_en_1177 = methodType ?? MethodTypeEN1177.UNKNOWN_METHOD_TYPE_EN_1177
    if (isLabTest !== null) {
      updatedSite.is_lab_test = isLabTest
    }
    if (executionDate !== null) {
      updatedSite.execution_date = executionDate.toDate().getTime()
    }
    if (address !== null) {
      updatedSite.address = address
    }
    if (userUUID !== null) {
      updatedSite.user_uuid = userUUID
    }
    if (pictures !== null) {
      updatedSite.pictures = pictures
    }
    updatedSite.updated_at = Date.now()
    updateSite(updatedSite)
    externallySetSite(updatedSite)
  }
  const debouncedOnUpdate = useDebounce(onUpdate)

  // UPDATE SITE ACK
  useEffect(() => {
    if (memoSiteUUID === null || updateSiteAck === null) {
      return
    }
    // UPD 2024-09-06: do not re-fetch site to save bandwidth
    // and to avoid input "glitch" when the user is typing
    //
    // use the externallySetSite() to update the local state
    //
    // // getSite(memoSiteUUID)
    console.log(`SitePage: updateSiteAck`, updateSiteAck.toObject())
    antdMessageCtx.success(translated("Site updated!"))
  }, [translated, memoSiteUUID, updateSiteAck])

  // TRIGGER UPDATE
  useEffect(() => {
    if (memoSiteIsMutated === false) {
      return
    }
    debouncedOnUpdate()
  }, [memoSiteIsMutated])

  const memoImpactMarkerCoordinates = useMemo((): LabeledTsCoordinate[] | null => {
    let _markers: LabeledTsCoordinate[] = []
    if (siteZoneImpactsMap === null) {
      return _markers
    }
    for (let [_, impacts] of Object.entries(siteZoneImpactsMap)) {
      for (let impact of impacts) {
        if (impact.latitude === 0 || impact.longitude === 0) {
          continue
        }
        let impactUUID = pbUUIDToUuid(impact.uuid)
        _markers.push({
          key: pbUUIDToUuid(impact.uuid),
          timestamp: impact.created_at,
          lat: impact.latitude,
          lng: impact.longitude,
          header: (
            <Link to={`/impacts/${impactUUID}`}>
              <b>Impact</b> <FontAwesomeIcon icon={faExternalLink} />
            </Link>
          ),
          description: (
            <FlexCol>
              <div>
                <b>Gmax:</b> {impact.impact_gmax.toFixed(0)}g
              </div>
              <div>
                <b>HIC:</b> {impact.impact_hic.toFixed(0)}
              </div>
            </FlexCol>
          ),
        })
      }
    }
    return _markers
  }, [siteZoneImpactsMap])

  const memoEffectiveDeviceNameElement = useMemo((): ReactElement => {
    if (siteZoneImpactsMap === null) {
      return <>N/A</>
    }
    let deviceNameCountMap: Record<string, number> = {}
    for (let [_, impacts] of Object.entries(siteZoneImpactsMap)) {
      for (let impact of impacts) {
        let sn = impact.metadata_station_serial_number
        if (sn === "") {
          continue
        }
        if (sn in deviceNameCountMap) {
          deviceNameCountMap[sn] += 1
        } else {
          deviceNameCountMap[sn] = 1
        }
      }
    }
    const sortedDeviceNameCountArray = Object.entries(deviceNameCountMap).sort(
      (a, b) => b[1] - a[1],
    )
    const mostFrequentDevice = sortedDeviceNameCountArray[0]
    if (mostFrequentDevice === undefined) {
      return <>N/A</>
    }
    const totalImpacts = Object.values(deviceNameCountMap).reduce((a, b) => a + b, 0)
    if (totalImpacts === 0) {
      return <>N/A</>
    }
    const ratio = mostFrequentDevice[1] / totalImpacts
    const perc = (ratio * 100).toFixed(1)
    const rows: ReactElement[] = []
    rows.push(
      <div style={{ fontSize: "1.125rem", color: "gray" }}>
        <b>{mostFrequentDevice[0]}</b> {ratio < 1 && `(${perc}%)`}
      </div>,
    )
    for (let i = 1; i < sortedDeviceNameCountArray.length; i++) {
      const device = sortedDeviceNameCountArray[i]!
      const devicePerc = ((device[1] / totalImpacts) * 100).toFixed(1)
      rows.push(
        <div style={{ fontSize: "0.7rem", color: "gray" }}>
          <i>
            {device[0]} ({devicePerc}%)
          </i>
        </div>,
      )
    }
    return <FlexCol style={{ gap: 2 }}>{rows}</FlexCol>
  }, [siteZoneImpactsMap])

  // Cloud: Enterprise customers
  const loadEnterpriseCustomersCB = useCallback(async () => {
    if (currentEnterprise === null) {
      console.warn(`SitePage: currentEnterprise is null, skipping loading enterprise customers`)
      return
    }
    let { entity: enterpriseCustomers } = await callCloudApiV2<ICloudCustomer[]>(
      `/enterprise/customers?enterprise_uuid=${currentEnterprise.UUID}`,
    )
    if (enterpriseCustomers === null) {
      console.error(`SitePage: error loading enterprise customers: enterpriseCustomers is null`)
      return
    }
    console.log(`SitePage: successfully loaded enterprise customers`, enterpriseCustomers)
    setEnterpriseCustomers(enterpriseCustomers)
  }, [currentEnterprise])

  // Cloud: enterprise users
  const loadEnterpriseUsersCB = useCallback(async () => {
    if (currentEnterprise === null) {
      console.warn(`SitePage: currentEnterprise is null, skipping loading enterprise users`)
      return
    }
    let { entity: enterpriseUsers } = await callCloudApiV2<ICloudUser[]>(
      `/enterprise/users?enterprise_uuid=${currentEnterprise.UUID}`,
    )
    if (enterpriseUsers === null) {
      console.error(`SitePage: error loading enterprise users: enterpriseUsers is null`)
      return
    }
    console.log(`SitePage: successfully loaded enterprise users`, enterpriseUsers)
    setEnterpriseUsers(enterpriseUsers)
  }, [currentEnterprise])

  // Cloud: site user
  const loadSiteUserCB = useCallback(async () => {
    if (userUUID === null) {
      console.warn(`SitePage: userUUID is null, skipping loading site user`)
      return
    }
    let { entity: siteUser } = await callCloudApiV2<ICloudUser>(
      `/user?user_uuid=${pbUUIDToUuid(userUUID)}`,
    )
    if (siteUser === null) {
      console.error(`SitePage: error loading site user: siteUser is null`)
      return
    }
    console.log(`SitePage: successfully loaded site user`, siteUser)
    setSiteUser(siteUser)
  }, [userUUID])

  useEffect(() => {
    let selectedCustomer = enterpriseCustomers.find((c) => c.Name === clientName)
    if (selectedCustomer) {
      setClientUUID(uuidToPbUUID(selectedCustomer.UUID))
    } else {
      setClientUUID(null)
    }
  }, [enterpriseCustomers, clientName])

  useEffect(() => {
    loadEnterpriseCustomersCB()
    loadEnterpriseUsersCB()
  }, [currentEnterprise])

  useEffect(() => {
    loadSiteUserCB()
  }, [userUUID])

  const memoUserSelectorOptions = useMemo(() => {
    const options = []
    if (siteUser !== null) {
      options.push({
        value: siteUser.UUID,
        label: `${siteUser.FirstName} ${siteUser.LastName} (${siteUser.Email})`,
      })
    }
    if (enterpriseUsers !== null) {
      for (let user of enterpriseUsers) {
        if (siteUser !== null && user.UUID === siteUser.UUID) {
          continue
        }
        options.push({
          value: user.UUID,
          label: `${user.FirstName} ${user.LastName} (${user.Email})`,
        })
      }
    }
    return options
  }, [siteUser, enterpriseUsers])

  const memoNormTypeSelectorOptions = useMemo(() => {
    return [
      {
        value: NormType.EN_1177,
        label: (
          <span
            style={{
              textTransform: "uppercase",
            }}
          >
            <Translated keyEn={normTypeDescriptionStr(NormType.EN_1177)} />
          </span>
        ),
      },
      {
        value: NormType.EN_12503,
        label: (
          <span
            style={{
              textTransform: "uppercase",
            }}
          >
            <Translated keyEn={normTypeDescriptionStr(NormType.EN_12503)} />
          </span>
        ),
      },
      {
        value: NormType.EN_14960,
        label: (
          <span
            style={{
              textTransform: "uppercase",
            }}
          >
            <Translated keyEn={normTypeDescriptionStr(NormType.EN_14960)} />
          </span>
        ),
      },
      {
        value: NormType.EN_ISO_23659,
        label: (
          <span
            style={{
              textTransform: "uppercase",
            }}
          >
            <Translated keyEn={normTypeDescriptionStr(NormType.EN_ISO_23659)} />
          </span>
        ),
      },
    ]
  }, [])

  const memoMethodTypeSelectorOptions = useMemo(() => {
    if (normType !== NormType.EN_1177) {
      return []
    }
    return [
      {
        value: MethodTypeEN1177.CRITICAL_FALL_HEIGHT_DETERMINATION,
        label: (
          <span
            style={{
              textTransform: "uppercase",
            }}
          >
            <Translated keyEn="Method" /> 1:{" "}
            <Translated
              keyEn={methodDescriptionStr(MethodTypeEN1177.CRITICAL_FALL_HEIGHT_DETERMINATION)}
            />
          </span>
        ),
      },
      {
        value: MethodTypeEN1177.IMPACT_ATTENUATION_COMPLIANCE,
        label: (
          <span
            style={{
              textTransform: "uppercase",
            }}
          >
            <Translated keyEn="Method" /> 2:{" "}
            <Translated
              keyEn={methodDescriptionStr(MethodTypeEN1177.IMPACT_ATTENUATION_COMPLIANCE)}
            />
          </span>
        ),
      },
    ]
  }, [normType])

  const memoClientAddress = useMemo(() => {
    if (clientUUID === null || enterpriseCustomers === null) {
      return ""
    }
    let selectedCustomer = enterpriseCustomers.find((c) => c.UUID === pbUUIDToUuid(clientUUID))
    if (selectedCustomer === undefined) {
      return ""
    }
    return selectedCustomer.PrimaryAddress
  }, [clientUUID, enterpriseCustomers])

  if (site === null) {
    return (
      <FlexCol
        style={{
          width: "100%",
          height: "60vh",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Spin size="large" />
      </FlexCol>
    )
  }

  return (
    <>
      {/* antd message context holder */}
      {contextHolder}
      <FlexCol
        style={{
          width: "100%",
          gap: CENTRAL_CONTAINER_GAP,
        }}
      >
        <SimplifiedBreadcrumb
          root="/sites/home"
          previousItems={[]}
          currentItem={{ label: siteName }}
        />
        <Row
          justify="space-between"
          style={{
            marginTop: "1rem",
          }}
        >
          <Col>
            <FlexCol style={{ gap: 0 }}>
              <Label>
                <Translated keyEn="Site" />
              </Label>
              <Typography.Text
                style={{
                  fontSize: "2rem",
                }}
              >
                {siteName}
              </Typography.Text>
            </FlexCol>
          </Col>
          <Col>
            <FlexCol style={{ gap: 3 }}>
              {memoNormTypeElement}
              {memoMethodElement}
              {memoIsLabTestElement}
            </FlexCol>
          </Col>
        </Row>
        {/* Site name */}
        <LabeledInput
          translatableLabel={translated("Site")}
          value={siteName}
          setValue={setSiteName}
        />
        {/* New equipment */}
        <div>
          <Label>
            <Translated keyEn="New equipment" />
          </Label>
          <Space.Compact
            style={{
              width: "100%",
            }}
          >
            <Input
              size="large"
              value={newEquipmentName ?? ""}
              onChange={(e) => {
                setNewEquipmentName(e.target.value)
              }}
              placeholder={translated("New equipment") ?? ""}
            />
            <NewEquipmentButton siteUUID={memoSiteUUID} equipmentName={newEquipmentName} />
          </Space.Compact>
        </div>
        {/* Site equipments */}
        <div>
          <Label>
            <Translated keyEn="Equipments" />
          </Label>
        </div>
        {memoEquipmentsTable}
        {/* Site picture */}
        <FlexCol style={{ gap: 0 }}>
          <Label>
            <Translated keyEn="Picture" />
          </Label>
          {pictures !== null && <UploadImageListV2 pictures={pictures} setPictures={setPictures} />}
        </FlexCol>
        {/* Site data */}
        <div>
          <Row
            gutter={[10, 10]}
            style={{
              marginTop: 20,
            }}
          >
            <Col xs={24} md={12}>
              <LabeledInput
                translatableLabel={translated("Mission")}
                value={missionName}
                setValue={setMission}
              />
            </Col>
            <Col xs={24} md={12}>
              <FlexCol style={{ gap: 0 }}>
                <Label>
                  <Translated keyEn="Execution date" />
                </Label>
                <DatePicker
                  size="large"
                  value={executionDate}
                  onChange={(value) => {
                    setExecutionDate(value)
                  }}
                  variant="filled"
                />
              </FlexCol>
            </Col>
            <Col xs={24} md={24}>
              <Label>
                <Translated keyEn="Client" />
              </Label>
              <AutoComplete
                size="large"
                variant="filled"
                value={clientName ?? ""}
                style={{ width: "100%" }}
                options={enterpriseCustomers.map((customer) => ({
                  value: customer.Name,
                }))}
                onChange={(value) => {
                  setClient(value)
                }}
                onSelect={(value) => {
                  let selectedCustomer = enterpriseCustomers.find((c) => c.Name === value)
                  if (selectedCustomer) {
                    setClient(selectedCustomer.Name)
                  }
                }}
                filterOption={(inputValue, option) =>
                  option!.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                }
              />
              <div
                style={{
                  fontStyle: "italic",
                  fontSize: "0.8rem",
                  color: "#888",
                }}
              >
                {memoClientAddress}
              </div>
            </Col>
            <Col xs={24}>
              <FlexCol style={{ gap: 0 }}>
                <Label>
                  <Translated keyEn="Address" />
                </Label>
                <GoogleAddressWidget
                  address={address}
                  setAddress={setAddress}
                  points={memoImpactMarkerCoordinates}
                />
              </FlexCol>
            </Col>
          </Row>
        </div>
        {/* Norm and method types swap */}
        <Row gutter={[10, 10]} style={{ marginTop: 20 }}>
          <Col xs={24} md={16}>
            <UnderlinedSectionTitle>
              <Translated keyEn="Norm type" />
            </UnderlinedSectionTitle>
            <FlexCol>
              {normType !== null && (
                <Compact style={{ width: "100%" }}>
                  <Select
                    value={selectedNormTypeToChange}
                    style={
                      {
                        // minWidth: "30rem",
                      }
                    }
                    options={memoNormTypeSelectorOptions}
                    onChange={(value) => {
                      setSelectedNormTypeToChange(value)
                    }}
                  />
                  <Button
                    type="primary"
                    disabled={normType === selectedNormTypeToChange}
                    onClick={() => {
                      setNormType(selectedNormTypeToChange)
                      antdMessageCtx.info(
                        <>
                          <Translated keyEn="Changed norm type to" />{" "}
                          <span
                            style={{
                              textTransform: "uppercase",
                            }}
                          >
                            <b>{normTypeDescriptionStr(selectedNormTypeToChange)}</b>
                          </span>
                        </>,
                      )
                    }}
                  >
                    <Translated keyEn="Apply" />
                  </Button>
                </Compact>
              )}
              {methodType !== null &&
                methodType !== MethodTypeEN1177.UNKNOWN_METHOD_TYPE_EN_1177 && (
                  <Compact style={{ width: "100%" }}>
                    <Select
                      value={selectedMethodTypeToChange}
                      style={{
                        minWidth: "15rem",
                      }}
                      options={memoMethodTypeSelectorOptions}
                      onChange={(value) => {
                        setSelectedMethodTypeToChange(value)
                      }}
                    />
                    <Button
                      type="primary"
                      disabled={methodType === selectedMethodTypeToChange}
                      onClick={() => {
                        setMethodType(selectedMethodTypeToChange)
                        antdMessageCtx.info(
                          <>
                            <Translated keyEn="Changed method type to" />{" "}
                            <span
                              style={{
                                textTransform: "uppercase",
                              }}
                            >
                              <b>{methodDescriptionStr(selectedMethodTypeToChange)}</b>
                            </span>
                          </>,
                        )
                      }}
                    >
                      <Translated keyEn="Apply" />
                    </Button>
                  </Compact>
                )}
            </FlexCol>
          </Col>
          <Col>
            <UnderlinedSectionTitle>
              <Translated keyEn="Device" />
            </UnderlinedSectionTitle>
            {memoEffectiveDeviceNameElement}
          </Col>
          <Col>
            <UnderlinedSectionTitle>
              <Translated keyEn="Inspector" />
            </UnderlinedSectionTitle>
            <Compact style={{ width: "100%" }}>
              <Select
                style={{
                  minWidth: "15rem",
                }}
                options={memoUserSelectorOptions}
                value={selectedUserUUID}
                onChange={(value) => {
                  setSelectedUserUUID(value)
                }}
              />
              <Button
                type="primary"
                disabled={
                  selectedUserUUID === null ||
                  userUUID === null ||
                  pbUUIDToUuid(userUUID) === selectedUserUUID
                }
                onClick={() => {
                  setUserUUID(uuidToPbUUID(selectedUserUUID!))
                  antdMessageCtx.info(
                    <>
                      <Translated keyEn="Changed inspector to" />{" "}
                      <b>
                        {memoUserSelectorOptions.find((o) => o.value === selectedUserUUID)?.label}
                      </b>
                    </>,
                  )
                }}
              >
                <Translated keyEn="Apply" />
              </Button>
            </Compact>
          </Col>
        </Row>
        {/* Annexes */}
        <UnderlinedSectionTitle>
          <Translated keyEn="Annexes" />
        </UnderlinedSectionTitle>
        <div>
          <FlexCol
            style={{
              alignItems: "start",
              gap: 0,
            }}
          >
            {memoAnnexesElements}
            <Button
              type="primary"
              size="large"
              onClick={onAddAnnex}
              style={{
                width: 200,
              }}
              icon={<FileAddOutlined />}
            >
              <span
                style={{
                  textTransform: "uppercase",
                }}
              >
                <Translated keyEn="Add annex" />
              </span>
            </Button>
          </FlexCol>
        </div>
        {/* Delete & Save */}
        <FlexRow
          style={{
            alignItems: "center",
            alignSelf: "flex-end",
          }}
        >
          <Popconfirm
            title={
              <>
                <Translated keyEn="Are you sure you want to delete this site" />?
              </>
            }
            onConfirm={onDelete}
          >
            <Button type="link" danger icon={<DeleteOutlined />} size="small">
              <span
                style={{
                  textTransform: "uppercase",
                  fontSize: "0.8rem",
                }}
              >
                <Translated keyEn="Delete" />
              </span>
            </Button>
          </Popconfirm>
          <div
            style={{
              width: 200,
            }}
          >
            <Button
              type="primary"
              icon={<SaveOutlined />}
              size="large"
              block
              disabled={!memoSiteIsMutated}
              onClick={onUpdate}
            >
              <span
                style={{
                  textTransform: "uppercase",
                }}
              >
                <Translated keyEn="Save" />
              </span>
            </Button>
          </div>
          <div
            style={{
              width: 200,
            }}
          >
            <Link to={`/report/${memoSiteUUID ? pbUUIDToUuid(memoSiteUUID) : null}`}>
              <Button type="primary" icon={<FilePdfOutlined />} size="large" block>
                <span
                  style={{
                    textTransform: "uppercase",
                  }}
                >
                  <Translated keyEn="Report" />
                </span>
              </Button>
            </Link>
          </div>
        </FlexRow>
        <EntityTimestamps entity={site} />
      </FlexCol>
      <DataTreeDrawer site={site} selectedKey={memoSiteUUID ? pbUUIDToUuid(memoSiteUUID) : null} />
    </>
  )
}
